import React from "react";
import { useTranslation } from "react-i18next";
import { Search, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogBody, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/Dialog";
import Button from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { Tooltip } from "@/components/ui/Tooltip";
import { useToast, ToastRenderer } from "@/hooks/useToast";
import {
  AssetCategoryView,
  EmployeeSimpleView,
  getCategories,
  searchEmployees,
  setEquipmentRequirementOverride,
} from "../../../../api/General/assets";
import { EquipmentPriority } from "../../../../api/General/businessRoles";
import { priorityLabelKey, priorityOrder } from "@/components/assets/equipmentRequirementChrome";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => Promise<void>;
}

const controlClasses =
  "w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground transition-colors focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20";

/**
 * Create an equipment requirement that no role or goal declared.
 *
 * The drawer's "Add employee" can only extend a category the recommendation already surfaced, and a
 * category is only surfaced when it has stock AND somebody already needs it. This is the way in for
 * everything else, so the asset manager is not blocked by what the engine happened to find.
 */
export default function AddRequirementDialog({ open, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();

  const [categories, setCategories] = React.useState<AssetCategoryView[]>([]);
  const [loadingCategories, setLoadingCategories] = React.useState(false);
  const [categoryId, setCategoryId] = React.useState<number | "">("");
  const [priority, setPriority] = React.useState<EquipmentPriority>(EquipmentPriority.Important);
  const [quantity, setQuantity] = React.useState(1);

  const [search, setSearch] = React.useState("");
  const [results, setResults] = React.useState<EmployeeSimpleView[]>([]);
  const [searching, setSearching] = React.useState(false);
  // A list, not one pick: the same add-several-in-a-row shape the asset form uses for serial numbers.
  const [selected, setSelected] = React.useState<EmployeeSimpleView[]>([]);

  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setCategoryId("");
    setPriority(EquipmentPriority.Important);
    setQuantity(1);
    setSearch("");
    setSelected([]);
    setResults([]);
  }, [open]);

  // Requirements are declared at the category level, so the SKUs underneath are not offered here.
  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingCategories(true);
    getCategories()
      .then((result) => {
        if (cancelled) return;
        setCategories(result.errors.length === 0 ? result.payload.filter((c) => c.level === 2) : []);
      })
      .catch(() => { if (!cancelled) setCategories([]); })
      .finally(() => { if (!cancelled) setLoadingCategories(false); });
    return () => { cancelled = true; };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const result = await searchEmployees(search.trim() || undefined);
        if (!cancelled) setResults(result.errors.length === 0 ? result.payload : []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, search]);

  const selectedIds = React.useMemo(() => new Set(selected.map((e) => e.id)), [selected]);
  const visibleResults = results.filter((r) => !selectedIds.has(r.id));
  const canSubmit = categoryId !== "" && selected.length > 0 && quantity > 0 && !submitting;

  /**
   * One call per employee, sequentially. A partial failure keeps ONLY the people it could not add
   * selected, so retrying does not silently re-add the ones that already succeeded.
   */
  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const failed: EmployeeSimpleView[] = [];
    try {
      for (const person of selected) {
        try {
          const result = await setEquipmentRequirementOverride({
            employeeId: person.id,
            categoryId: Number(categoryId),
            countsTowardCoverage: true,
            isAdded: true,
            priority,
            quantity,
          });
          if (result.errors.length > 0) failed.push(person);
        } catch {
          failed.push(person);
        }
      }

      const added = selected.length - failed.length;
      if (added > 0) await onCreated();

      if (failed.length === 0) {
        showToast(t("assetOverview.addRequirementDone", { count: added, name: selected[0]!.fullName }), "success");
        onClose();
      } else if (added > 0) {
        showToast(t("assetOverview.addRequirementPartial", {
          done: added, total: selected.length, names: failed.map((f) => f.fullName).join(", "),
        }), "error");
        setSelected(failed);
      } else {
        showToast(t("assetOverview.overrideError"), "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const guard = (e: Event) => { if (submitting) e.preventDefault(); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onClose(); }}>
      <DialogContent onEscapeKeyDown={guard} onPointerDownOutside={guard} data-testid="add-requirement-modal">
        <DialogHeader>
          <DialogTitle>{t("assetOverview.addRequirement")}</DialogTitle>
          <DialogDescription>{t("assetOverview.addRequirementDescription")}</DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-5">
            <div className="space-y-1.5">
              <label htmlFor="add-req-category" className="text-sm font-medium text-foreground">
                {t("assetOverview.addRequirementCategory")}
              </label>
              {loadingCategories ? (
                <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                  <Spinner size="sm" />
                  {t("assetOverview.addRequirementLoadingCategories")}
                </div>
              ) : (
                <select
                  id="add-req-category"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value === "" ? "" : Number(e.target.value))}
                  className={controlClasses}
                  data-testid="add-requirement-category"
                >
                  <option value="">{t("assetOverview.addRequirementCategoryPlaceholder")}</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.parentName ? `${c.parentName} / ${c.name}` : c.name}
                    </option>
                  ))}
                </select>
              )}
              {!loadingCategories && categories.length === 0 && (
                <p className="text-xs text-muted-foreground" data-testid="add-requirement-no-categories">
                  {t("assetOverview.addRequirementNoCategories")}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <span className="block text-sm font-medium text-foreground">
                {t("assetOverview.addRequirementEmployees")}
              </span>

              {/* Chosen people sit above the search as removable chips, the same shape the asset form
                  uses for serial numbers, so the field stays free to pick the next one. */}
              {selected.length > 0 && (
                <div className="flex flex-wrap gap-2 pb-1" data-testid="add-requirement-selected">
                  {selected.map((person) => (
                    <span
                      key={person.id}
                      className="inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2 py-1 text-sm text-primary"
                      data-testid={`add-requirement-chip-${person.id}`}
                    >
                      <Check className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      <span className="truncate">{person.fullName}</span>
                      <button
                        type="button"
                        onClick={() => setSelected((prev) => prev.filter((x) => x.id !== person.id))}
                        aria-label={t("assetOverview.addRequirementRemoveEmployee", { name: person.fullName })}
                        className="text-muted-foreground transition-colors hover:text-destructive focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 rounded"
                        data-testid={`add-requirement-chip-remove-${person.id}`}
                      >
                        <X className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <>
                  <div className="relative">
                    <Search className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground ltr:left-3 rtl:right-3" aria-hidden="true" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={t("assetOverview.addEmployeeSearch")}
                      aria-label={t("assetOverview.addEmployeeSearch")}
                      className={`${controlClasses} ltr:pl-9 rtl:pr-9`}
                      data-testid="add-requirement-search"
                    />
                  </div>
                  {searching ? (
                    <div className="flex justify-center py-3"><Spinner size="sm" /></div>
                  ) : (
                    <ul className="max-h-40 space-y-0.5 overflow-y-auto">
                      {visibleResults.map((r) => (
                        <li key={r.id}>
                          <button
                            type="button"
                            onClick={() => setSelected((prev) => [...prev, r])}
                            className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-start text-sm text-foreground transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                            data-testid={`add-requirement-result-${r.id}`}
                          >
                            <span className="min-w-0 truncate">{r.fullName}</span>
                            {r.department && <span className="shrink-0 text-xs text-muted-foreground">{r.department}</span>}
                          </button>
                        </li>
                      ))}
                      {visibleResults.length === 0 && (
                        <li className="px-2 py-3 text-center text-xs text-muted-foreground" data-testid="add-requirement-no-results">
                          {t("assetOverview.addEmployeeNoResults")}
                        </li>
                      )}
                    </ul>
                  )}
              </>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label htmlFor="add-req-priority" className="text-sm font-medium text-foreground">
                  {t("assetOverview.addRequirementPriority")}
                </label>
                <select
                  id="add-req-priority"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as EquipmentPriority)}
                  className={controlClasses}
                  data-testid="add-requirement-priority"
                >
                  {priorityOrder.map((p) => (
                    <option key={p} value={p}>{t(priorityLabelKey[p])}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <label htmlFor="add-req-quantity" className="text-sm font-medium text-foreground">
                  {t("assetOverview.addRequirementQuantity")}
                </label>
                <input
                  id="add-req-quantity"
                  type="number"
                  min={1}
                  max={99}
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Math.min(99, Number(e.target.value) || 1)))}
                  className={`${controlClasses} tabular-nums`}
                  data-testid="add-requirement-quantity"
                />
              </div>
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            {t("Cancel")}
          </Button>
          <Tooltip content={t("assetOverview.addRequirementSubmitTooltip")}>
            <Button onClick={submit} disabled={!canSubmit} data-testid="add-requirement-submit">
              {submitting && <Spinner size="sm" className="me-2" />}
              {selected.length === 0
                ? t("assetOverview.addRequirementSubmit")
                : t("assetOverview.addRequirementSubmitCount", { count: selected.length })}
            </Button>
          </Tooltip>
        </DialogFooter>
        <ToastRenderer toast={toast} onDismiss={dismissToast} />
      </DialogContent>
    </Dialog>
  );
}
